import { env } from '@/config/env';
import axios, { AxiosInstance } from 'axios';
import { cookieService } from '@/shared/infrastructure/storage/cookie.service';
import { TokenInfo, OAuth2TokenResponse, OAuth2UserInfo } from '../../domain/entities/user.entity';

/**
 * Service for OAuth2 authentication and authorization
 * Implements the Authorization Code and Client Credentials flows
 */
export class OAuth2Service {
  private axiosInstance: AxiosInstance;
  private readonly baseURL: string;
  private readonly clientId: string;
  private readonly clientSecret: string;
  private readonly redirectUri: string;
  
  // Access token almacenado en memoria (no persistente)
  private accessToken: string | null = null;
  private tokenType: string = 'Bearer';
  private expiresAt: number = 0;
  private scope: string | undefined;

  constructor() {
    this.baseURL = env.OAUTH2_BASE_URL;
    this.clientId = env.OAUTH2_CLIENT_ID;
    this.clientSecret = env.OAUTH2_CLIENT_SECRET;
    this.redirectUri = env.OAUTH2_REDIRECT_URI;

    this.axiosInstance = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  /**
   * Login with username and password
   * @param username - The username
   * @param password - The password
   * @returns The tokens
   */
  async login(username: string, password: string): Promise<any> {
    try {
      const { data } = await this.axiosInstance.post<{ code: string; state?: string }>(
       `${env.OAUTH_URL_LAMBDA}/login`,
        {
          username,
          password,
          useWebportal: true, // Usar configuración de webportal
        },
      );
      return data || {};
    }
    catch (error) {
      console.error('Error al iniciar sesión:', error);
      throw error;
    }
  }

  /**
   * Refresh an access token using the refresh token
   * @param refreshToken - The refresh token
   * @returns The tokens
   */
  async refreshToken(refreshToken: string): Promise<TokenInfo> {
    const response = await this.axiosInstance.post<any>(
      `${env.OAUTH_URL_LAMBDA}/refresh`,
      {
        refresh_token: refreshToken,
        useWebportal: true, // Usar configuración de webportal
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    // La lambda devuelve el response dentro de data si es exitoso
    const tokenData = response.data.success ? response.data : response.data;

    return this.processTokenResponse(tokenData);
  }

  /**
   * get user information
   * @param accessToken - The access token
   * @returns The user information
   */
  async getUserInfo(accessToken: string): Promise<OAuth2UserInfo> {
    const response = await this.axiosInstance.get<OAuth2UserInfo>(
      `${env.OAUTH_URL_LAMBDA}/userinfo`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    const data: any = response.data;
    const userInfo: any = data?.userinfo || data;

    if (!userInfo.username && userInfo.preferred_username) {
      userInfo.username = userInfo.preferred_username;
    }

    if (!userInfo.name) {
      const fullName = [userInfo.given_name, userInfo.family_name]
        .filter(Boolean)
        .join(' ');
      if (fullName) {
        userInfo.name = fullName;
      }
    }

    return userInfo;
  }

  /**
   * Introspect a token (validate and get information)
   * @param token - The token
   * @returns The introspection result
   */
  async introspectToken(token: string): Promise<any> {
    const params = new URLSearchParams({
      token,
      client_id: this.clientId.trim(),
      client_secret: this.clientSecret.trim(),
    });

    const response = await this.axiosInstance.post(
      '/oauth/introspect',
      params.toString(),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }
    );

    return response.data;
  }

  /**
   * Revoke a token (logout)
   * @param token - The token
   * @param tokenTypeHint - The token type hint
   * @returns The void
   */
  async revokeToken(token: string, tokenTypeHint: 'access_token' | 'refresh_token' = 'access_token'): Promise<void> {
    try {
      const body = new URLSearchParams({ token: token }).toString();
      await this.axiosInstance.post<void>('/logout', body, {
        baseURL: `${env.OAUTH_URL_LAMBDA}`,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': `Bearer ${token}`,
        },
      });
    } catch (error) {
      console.error('Error al hacer logout remoto:', error);
    }
  }

  /**
   * Process the token response and calculate expires_at
   * @param data - The token response
   * @returns The token information
   */
  private processTokenResponse(data: OAuth2TokenResponse): TokenInfo {
    const expiresIn = data.expires_in || 3600; // Default 1 hour
    const expiresAt = Date.now() + expiresIn * 1000;

    return {
      access_token: data.access_token,
      token_type: data.token_type || 'Bearer',
      expires_at: expiresAt,
      refresh_token: data.refresh_token,
      scope: data.scope,
    };
  }

  /**
   * Save tokens using the double token strategy:
   * - Access Token: In memory (short-lived, for API requests)
   * - Refresh Token: In HttpOnly cookie (long-lived, for token refresh)
   * 
   * @param tokenInfo - The token information
   */
  saveTokens(tokenInfo: TokenInfo): void {
    try {
      // Access Token: Almacenar en memoria (no persistente)
      this.accessToken = tokenInfo.access_token;
      this.tokenType = tokenInfo.token_type || 'Bearer';
      this.expiresAt = tokenInfo.expires_at;
      this.scope = tokenInfo.scope;

      // Refresh Token: Almacenar en cookie HttpOnly (si está disponible)
      // NOTA: HttpOnly solo puede ser establecido por el servidor.
      // Aquí usamos una cookie segura con Secure y SameSite como alternativa.
      if (tokenInfo.refresh_token) {
        // Calcular expiración: 30 días por defecto (o usar expires_in si está disponible)
        const refreshTokenExpiresIn = 30 * 24 * 60 * 60; // 30 días en segundos
        
        cookieService.set('oauth2_refresh_token', tokenInfo.refresh_token, {
          maxAge: refreshTokenExpiresIn,
          path: '/',
          secure: env.IS_PROD || window.location.protocol === 'https:', // Solo HTTPS en producción
          sameSite: 'Lax', // Protección CSRF, permite navegación cross-site
          // httpOnly: true, // No funciona desde JS - el backend debe establecer esto
        });
      }
    } catch (error) {
      console.error('Error al guardar tokens:', error);
      throw error;
    }
  }

  /**
   * Restore session from refresh token cookie
   * This is called when the app initializes to restore the session after page reload
   * @returns Promise<TokenInfo | null>
   */
  async restoreSession(): Promise<TokenInfo | null> {
    try {
      // Verificar si hay refresh token en la cookie
      const refreshToken = cookieService.get('oauth2_refresh_token');
      
      if (!refreshToken) {
        console.log('No hay refresh token en cookie, no se puede restaurar sesión');
        return null;
      }

      // Si ya hay un access token válido en memoria, no es necesario restaurar
      if (this.accessToken && this.expiresAt && this.expiresAt > Date.now()) {
        console.log('Access token válido en memoria, no es necesario restaurar');
        return this.getTokens();
      }
      
      // Intentar refrescar el token usando el refresh token de la cookie
      try {
        const newTokens = await this.refreshToken(refreshToken);
        this.saveTokens(newTokens);
        return newTokens;
      } catch (error: any) {
        // Si el refresh token es inválido o expirado, limpiar la cookie
        const errorMessage = error?.response?.data?.error || error?.message || 'Error desconocido';
        const errorStatus = error?.response?.status;
        
        console.warn(`No se pudo restaurar la sesión (${errorStatus}):`, errorMessage);
        
        // Limpiar cookie si el token es inválido o expirado
        if (errorStatus === 401 || errorStatus === 400) {
          console.log('Refresh token inválido o expirado, limpiando cookie');
          cookieService.remove('oauth2_refresh_token', { path: '/' });
        }
        
        return null;
      }
    } catch (error) {
      console.error('Error al restaurar sesión:', error);
      return null;
    }
  }

  /**
   * Get tokens from memory and cookie
   * Access Token: from memory
   * Refresh Token: from cookie
   * 
   * Si no hay access token pero hay refresh token, intenta restaurar la sesión automáticamente
   */
  getTokens(): TokenInfo | null {
    try {
      // Access Token: desde memoria
      if (!this.accessToken || !this.expiresAt) {
        // Si no hay access token pero hay refresh token en cookie, 
        // la sesión se restaurará automáticamente cuando se intente usar
        const refreshToken = cookieService.get('oauth2_refresh_token');
        if (refreshToken) {
          // Retornar null para que el interceptor de http-client intente restaurar
          return null;
        }
        return null;
      }

      // Refresh Token: desde cookie
      const refreshToken = cookieService.get('oauth2_refresh_token') || undefined;

      return {
        access_token: this.accessToken,
        token_type: this.tokenType,
        expires_at: this.expiresAt,
        refresh_token: refreshToken,
        scope: this.scope,
      };
    } catch (error) {
      console.error('Error al obtener tokens:', error);
      return null;
    }
  }

  /**
   * Verify if the token is expired
   * @param tokenInfo - The token information
   * @returns The boolean
   */
  isTokenExpired(tokenInfo: TokenInfo | null = null): boolean {
    const tokens = tokenInfo || this.getTokens();
    if (!tokens) {
      return true;
    }

    return tokens.expires_at < Date.now() + 5 * 60 * 1000;
  }

  /**
   * Clear tokens from memory and cookie
   */
  clearTokens(): void {
    try {
      // Limpiar access token de memoria
      this.accessToken = null;
      this.tokenType = 'Bearer';
      this.expiresAt = 0;
      this.scope = undefined;

      // Limpiar refresh token de cookie
      cookieService.remove('oauth2_refresh_token', { path: '/' });
    } catch (error) {
      console.error('Error al limpiar tokens:', error);
    }
  }
}

export const oauth2Service = new OAuth2Service();

